import React from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAPI } from '@hooks/useAPI'
import { getAchievementById } from '@services/api/achievements'
import { formatDate } from '../../utils/date'
import AchievementCard from '@components/business/AchievementCard'
import ImageGallery from '@components/business/ImageGallery'
import Image from '@components/ui/Image'
import Loading from '@components/ui/Loading'
import Button from '@components/ui/Button'
import { getFullAttachmentUrl, processRichTextImages } from '@services/api/index.js'

const AchievementDetail = () => {
  const { t, i18n } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()

  // API调用
  const {
    data: achievementData,
    loading,
    error,
    refetch
  } = useAPI(() => getAchievementById(id, i18n.language), [id, i18n.language])

  const handleBackToList = () => {
    navigate('/achievements')
  }

  const handleAchievementClick = (achievementId) => {
    navigate(`/achievements/${achievementId}`)
  }

  const getCategoryIcon = (category) => {
    const icons = {
      'project': '🏗️',
      'research': '📊',
      'cooperation': '🤝',
      'training': '📚',
      'exhibition': '🎨',
      'publication': '📖',
      'event': '🎯'
    }
    return icons[category] || '📋'
  }

  const getCategoryName = (category) => {
    const names = {
      'project': t('achievements.categories.project'),
      'research': t('achievements.categories.research'),
      'cooperation': t('achievements.categories.cooperation'),
      'training': t('achievements.categories.training'),
      'exhibition': t('achievements.categories.exhibition'),
      'publication': t('achievements.categories.publication'),
      'event': t('achievements.categories.event')
    }
    return names[category] || category
  }

  const formatParticipants = (participants) => {
    if (!Array.isArray(participants) || participants.length === 0) return ''
    return participants.join('、')
  }

  if (loading) {
    return (
      <div className="container section-padding">
        <div className="flex justify-center items-center min-h-[400px]">
          <Loading />
        </div>
      </div>
    )
  }

  if (error || !achievementData) {
    return (
      <div className="container section-padding">
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium">{t('achievements.states.loadFailed')}</p>
            <p className="text-sm text-gray-500 mt-2">{error || t('achievements.states.notFound')}</p>
          </div>
                      <div className="space-x-4">
              <Button onClick={handleBackToList}>{t('achievements.detail.actions.backToList')}</Button>
              <Button variant="outline" onClick={() => refetch()}>{t('achievements.states.retry')}</Button>
            </div>
        </div>
      </div>
    )
  }

  const achievement = achievementData
  const relatedAchievements = []

  // 处理富文本内容中的图片路径
  const processedContent = processRichTextImages(achievement.content);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container section-padding py-6">


        {/* 返回按钮 */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={handleBackToList}
            className="flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('achievements.detail.actions.backToList')}
          </Button>
        </div>

        {/* 成果标题 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-3 text-center">
            {achievement.title}
          </h1>
        </div>

        {/* 成果详细内容 */}
        {processedContent && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: processedContent }} />
          </div>
        )}

        {/* 相关成果推荐 */}
        {relatedAchievements.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              {t('achievements.detail.sections.relatedAchievements')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedAchievements.map((relatedAchievement) => (
                <AchievementCard
                  key={relatedAchievement.id}
                  achievement={relatedAchievement}
                  onClick={handleAchievementClick}
                  variant="compact"
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AchievementDetail
